import { Avatar, brandAvatarTint } from '@/components/ui/avatar';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { Hourglass, ShieldCheck } from 'iconoir-react';
import { BREATHE_ANIMATION } from './boost-animation';

export interface BoostIdentity {
  brandName: string;
  brandAvatarUrl?: string | null;
  username: string;
  creatorAvatarUrl?: string | null;
}

interface BoostAuthorizationHandshakeProps extends BoostIdentity {
  /**
   * The state chip between the two identities: `shield` frames the upcoming
   * authorization ask (preflight), `hourglass` a request already in flight,
   * breathing while the creator has not answered yet.
   */
  icon?: 'shield' | 'hourglass';
  title?: string;
  description?: string;
}

/**
 * The authorization hero: the brand and the creator, joined by dashed
 * connectors around a single state chip. The relationship carries the story —
 * "you are asking them" (shield) or "you asked, they have not answered yet"
 * (hourglass) — instead of a flat status box. Optional title + description
 * lines sit under the identities for the pending state.
 */
export function BoostAuthorizationHandshake({
  brandName,
  brandAvatarUrl,
  username,
  creatorAvatarUrl,
  icon = 'hourglass',
  title,
  description,
}: BoostAuthorizationHandshakeProps) {
  const isPending = icon === 'hourglass';
  const Icon = isPending ? Hourglass : ShieldCheck;

  return (
    <VStack gap={3.5} py={1}>
      <HStack gap={3} align="center" justify="center">
        <Avatar {...brandAvatarTint} size="md" name={brandName} src={brandAvatarUrl ?? undefined} />

        <DashedConnector />

        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxSize="28px"
          rounded="full"
          flexShrink={0}
          bg={isPending ? 'blue.50' : 'gray.100'}
          border="1px solid"
          borderColor={isPending ? 'blue.200' : 'gray.200'}
          color={isPending ? 'blue.600' : 'gray.600'}
          animation={isPending ? BREATHE_ANIMATION : undefined}
          aria-hidden="true"
        >
          <Icon width={13} height={13} />
        </Box>

        <DashedConnector />

        <Avatar size="md" name={username} src={creatorAvatarUrl ?? undefined} />
      </HStack>

      {(title || description) && (
        <VStack gap={1} maxW="300px">
          {title && (
            <Text fontSize="sm" fontWeight="semibold" color="gray.900" textAlign="center">
              {title}
            </Text>
          )}
          {description && (
            <Text fontSize="xs" color="gray.500" textAlign="center" lineHeight="1.5">
              {description}
            </Text>
          )}
        </VStack>
      )}
    </VStack>
  );
}

function DashedConnector() {
  return <Box w="44px" borderTop="1.5px dashed" borderColor="gray.300" aria-hidden="true" />;
}
